// Shared structured data for the local business.
// LocalBusiness + WebSite + organisation schema = strong knowledge-panel signal
// to Google. Every page injects ORG_JSONLD via the SEO component.

const SITE_URL = "https://yellowvideoproduction.com";
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const SERVICE_OFFERS = [
  {
    name: "Video Production Liverpool",
    description: "Filming, editing and motion graphics with free stills photography included.",
    url: `${SITE_URL}/video-production-in-liverpool`,
  },
  {
    name: "Photography Liverpool",
    description: "Commercial photography for products, portraits, events, fashion and property across the UK.",
    url: `${SITE_URL}/photography-liverpool`,
  },
  {
    name: "Animation Liverpool",
    description: "2D motion graphics, 3D visualisations and explainer animations for promotional and instructional content.",
    url: `${SITE_URL}/animation-liverpool`,
  },
  {
    name: "Drone Filming Liverpool",
    description: "CAA-licensed aerial filming and photography for property, events, construction and marketing.",
    url: `${SITE_URL}/drone-filming-aerial-photography-liverpool`,
  },
  {
    name: "White Label Video Production for Agencies",
    description: "Invisible video production delivered under your agency's brand. NDA-friendly, UK-wide.",
    url: `${SITE_URL}/white-label`,
  },
];

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService", "Organization"],
  "@id": ORG_ID,
  name: "Yellow Video Production",
  alternateName: ["Yellow", "Yellow Video", "YELLOW Video Production"],
  legalName: "New Reality Media Limited",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/yellow-logo.png`,
    width: 600,
    height: 600,
  },
  image: `${SITE_URL}/yellow-logo.png`,
  description:
    "Yellow Video Production is a Liverpool-based video production company providing videographers, editors, photographers and drone pilots across the entire UK. Over 1,000 films delivered for brands, charities, universities and government agencies.",
  slogan: "Video production that feels like cinema.",
  foundingDate: "2012",
  founder: { "@type": "Person", name: "Dave Knight" },
  telephone: "+44 151 427 2900",
  email: "info@yellowvideoproduction.com",
  priceRange: "££",
  currenciesAccepted: "GBP",
  paymentAccepted: ["Bank Transfer", "Credit Card", "Invoice"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "2nd Floor, Consortia House, 6 Columbus Quay",
    addressLocality: "Liverpool",
    addressRegion: "Merseyside",
    postalCode: "L3 4DB",
    addressCountry: "GB",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 53.4084,
    longitude: -2.9916,
  },
  hasMap: "https://www.google.com/maps/place/Consortia+House,+6+Columbus+Quay,+Liverpool+L3+4DB",
  areaServed: [
    { "@type": "City", name: "Liverpool" },
    { "@type": "City", name: "Manchester" },
    { "@type": "City", name: "Chester" },
    { "@type": "City", name: "London" },
    { "@type": "AdministrativeArea", name: "Merseyside" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "Ireland" },
  ],
  serviceType: [
    "Video Production",
    "Videography",
    "Photography",
    "Animation",
    "Motion Graphics",
    "Drone Filming",
    "Aerial Photography",
    "Video Editing",
    "White Label Video Production",
  ],
  knowsAbout: [
    "video production",
    "corporate video",
    "brand films",
    "product photography",
    "commercial photography",
    "drone aerial filming",
    "motion graphics animation",
    "3D visualisation",
    "video editing",
    "internal communications video",
    "explainer animation",
    "Liverpool video production",
    "UK video production",
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:30",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+44 151 427 2900",
      email: "info@yellowvideoproduction.com",
      contactType: "customer service",
      areaServed: "GB",
      availableLanguage: ["English"],
    },
  ],
  sameAs: [
    "https://www.facebook.com/YellowVideoProduction/",
    "https://twitter.com/yellowmp",
    "https://uk.linkedin.com/company/new-reality-media",
    "https://vimeo.com/yellowvideoproduction",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Video Production & Visual Content Services",
    itemListElement: SERVICE_OFFERS.map((s) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: s.name,
        description: s.description,
        url: s.url,
        provider: { "@id": ORG_ID },
      },
    })),
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "47",
    bestRating: "5",
    worstRating: "1",
  },
};

// WebSite schema with SearchAction.
// Tells Google about the site as a search target. Pairs with sitelinks
// search box in SERPs and helps the brand show up as a knowledge-panel entity.
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: "Yellow Video Production",
  description: "Liverpool video production company since 2012. UK wide.",
  publisher: { "@id": ORG_ID },
  inLanguage: "en-GB",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// Helper for Service-page JSON-LD.
// Now richer: areaServed includes UK, offers + priceRange show up in SERP,
// provider linked to the Organisation node for graph cohesion.
export function serviceJsonLd({ name, description, path, includeOffer = true }) {
  const node = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${path}`,
    provider: { "@id": ORG_ID },
    areaServed: [
      { "@type": "City", name: "Liverpool" },
      { "@type": "AdministrativeArea", name: "Merseyside" },
      { "@type": "Country", name: "United Kingdom" },
    ],
    serviceType: name,
    audience: { "@type": "BusinessAudience", name: "Businesses, brands, agencies, charities, universities" },
  };
  if (includeOffer) {
    node.offers = {
      "@type": "Offer",
      priceCurrency: "GBP",
      priceRange: "££",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/contact`,
    };
  }
  return node;
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}
